/**
 * Response Parser Service
 *
 * Transforms ContentProcessorResponse from the API (snake_case) to
 * ExtractionResults (camelCase) for use in the frontend application.
 *
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5, 11.3
 */

import type {
  ContentProcessorResponse,
  ContentProcessResponse,
  ExtractedComparisonItem as ApiExtractedComparisonItem,
  PaginatedApiResponse,
  StatusApiResponse,
  SchemaResponse,
  StepOutput,
} from '../types/api';
import type {
  ExtractionResults,
  ProcessingStatus,
  GroundRiskAnswer,
  GroundRiskMitigation,
  AirRiskAnswer,
  AirRiskMitigation,
  ConfidenceLevel,
  MitigationLevel,
  GroundRiskField,
  AirRiskField,
  GroundRiskMitigationCode,
  AirRiskMitigationCode,
  Citation,
} from '../types/extraction';
import type {
  ProcessedDocument,
  PaginatedResponse,
  Schema,
  ProcessStepOutput,
  ExtractionComparisonItem,
} from '../types/documents';

// Re-export ExtractedComparisonItem for backward compatibility
export type { ApiExtractedComparisonItem as ExtractedComparisonItem };

// ============================================================================
// Field Mapping Constants
// ============================================================================

/**
 * Mapping of API field names to ground risk field types
 */
const GROUND_RISK_FIELD_MAP: Record<string, GroundRiskField> = {
  'max_dimensions': 'max_dimensions',
  'maximum_dimensions': 'max_dimensions',
  'max_speed_mps': 'max_speed_mps',
  'maximum_speed': 'max_speed_mps',
  'weight_over_250g': 'weight_over_250g',
  'weight_above_250g': 'weight_over_250g',
  'controlled_ground_area_only': 'controlled_ground_area_only',
  'controlled_ground_area': 'controlled_ground_area_only',
  'max_population_density': 'max_population_density',
  'maximum_population_density': 'max_population_density',
  'population_density': 'max_population_density',
};

/**
 * Mapping of API field names to air risk field types
 */
const AIR_RISK_FIELD_MAP: Record<string, AirRiskField> = {
  'aae_only': 'aae_only',
  'atypical_air_environment': 'aae_only',
  'atypical_air_environment_only': 'aae_only',
  'known_ifp_area': 'known_ifp_area',
  'instrument_flight_procedures': 'known_ifp_area',
  'ifp_traffic_area': 'known_ifp_area',
  'vfr_corridor_area': 'vfr_corridor_area',
  'vfr_corridor': 'vfr_corridor_area',
  'vfr_corridor_low_level_helicopters': 'vfr_corridor_area',
  'above_500ft_agl': 'above_500ft_agl',
  'above_500ft': 'above_500ft_agl',
  'flying_above_500ft': 'above_500ft_agl',
  'operation_type': 'operation_type',
  'type_of_operation': 'operation_type',
};

/**
 * Mapping of API field names to ground risk mitigation codes
 */
const GROUND_MITIGATION_CODE_MAP: Record<string, GroundRiskMitigationCode> = {
  'm1a': 'M1A',
  'M1A': 'M1A',
  'sheltering': 'M1A',
  'm1a_sheltering': 'M1A',
  'm1b': 'M1B',
  'M1B': 'M1B',
  'operational_restrictions': 'M1B',
  'm1b_operational_restrictions': 'M1B',
  'm1c': 'M1C',
  'M1C': 'M1C',
  'ground_observations': 'M1C',
  'm1c_ground_observation': 'M1C',
  'm2': 'M2',
  'M2': 'M2',
  'reducing_impact_dynamics': 'M2',
  'impact_dynamics': 'M2',
  'm2_reducing_impact_dynamics': 'M2',
};

/**
 * Mapping of API field names to air risk mitigation codes
 */
const AIR_MITIGATION_CODE_MAP: Record<string, AirRiskMitigationCode> = {
  'sm1': 'SM1',
  'SM1': 'SM1',
  'boundary_restriction': 'SM1',
  'sm2': 'SM2',
  'SM2': 'SM2',
  'chronology_restriction': 'SM2',
  'sm3': 'SM3',
  'SM3': 'SM3',
  'exposure_time': 'SM3',
  'sm4': 'SM4',
  'SM4': 'SM4',
  'special_use_airspace': 'SM4',
  'sm5': 'SM5',
  'SM5': 'SM5',
  'airspace_requirements': 'SM5',
  'sm6': 'SM6',
  'SM6': 'SM6',
  'ansp_services': 'SM6',
  'sm7': 'SM7',
  'SM7': 'SM7',
  'utm_services': 'SM7',
  'sm8': 'SM8',
  'SM8': 'SM8',
  'notam': 'SM8',
  'sm9': 'SM9',
  'SM9': 'SM9',
  'military_notification': 'SM9',
  'sm10': 'SM10',
  'SM10': 'SM10',
  'local_outreach': 'SM10',
};

/**
 * Display names for ground risk mitigation codes
 */
const GROUND_MITIGATION_NAMES: Record<GroundRiskMitigationCode, string> = {
  'M1A': 'Sheltering',
  'M1B': 'Operational Restrictions',
  'M1C': 'Ground Observations',
  'M2': 'Reducing impact dynamics',
};

/**
 * Display names for air risk mitigation codes
 */
const AIR_MITIGATION_NAMES: Record<AirRiskMitigationCode, string> = {
  'SM1': 'Operational restriction by boundary',
  'SM2': 'Operational restriction by chronology',
  'SM3': 'Operational restriction by time of exposure',
  'SM4': 'Special Use Airspace (SUA)',
  'SM5': 'Other airspace requirements',
  'SM6': 'Pre-agreement of ANSP services',
  'SM7': 'Pre-agreement of UTM services',
  'SM8': 'NOTAM of intended operation',
  'SM9': 'Military low flying notification',
  'SM10': 'Outreach to local flying clubs and pilots',
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Convert a numeric confidence score (0-1) to a confidence level
 */
/**
 * Convert a confidence value (numeric 0-1 or string like "High", "Medium", "Low") to a confidence level
 */
export const confidenceToLevel = (confidence: number | string): ConfidenceLevel => {
  // Handle string confidence values (from API)
  if (typeof confidence === 'string') {
    const normalized = confidence.toLowerCase().trim();
    if (normalized === 'high' || normalized === 'h') {
      return 'high';
    } else if (normalized === 'medium' || normalized === 'med' || normalized === 'm') {
      return 'medium';
    } else if (normalized === 'low' || normalized === 'l') {
      return 'low';
    }
    // Try to parse as number
    const parsed = parseFloat(confidence);
    if (!isNaN(parsed)) {
      return confidenceToLevel(parsed);
    }
    return 'low';
  }

  // Handle numeric confidence values
  if (confidence >= 0.8) {
    return 'high';
  } else if (confidence >= 0.5) {
    return 'medium';
  }
  return 'low';
};

/**
 * Parse a string value to a mitigation level
 */
export const parseMitigationLevel = (value: string): MitigationLevel => {
  const normalized = value.toLowerCase().trim();

  if (
    normalized === 'low' ||
    normalized === 'l' ||
    normalized === 'low robustness'
  ) {
    return 'low';
  } else if (
    normalized === 'medium' ||
    normalized === 'med' ||
    normalized === 'm' ||
    normalized === 'medium robustness'
  ) {
    return 'medium';
  } else if (
    normalized === 'high' ||
    normalized === 'h' ||
    normalized === 'high robustness'
  ) {
    return 'high';
  } else if (
    normalized === 'not_applicable' ||
    normalized === 'n/a' ||
    normalized === 'na' ||
    normalized === 'not applicable' ||
    normalized === 'not selected'
  ) {
    return 'not_applicable';
  }

  // Default to not_applicable if unrecognized
  return 'not_applicable';
};


/**
 * Parse a string value to a boolean
 */
export const parseBoolean = (value: string): boolean => {
  const normalized = value.toLowerCase().trim();
  return (
    normalized === 'true' ||
    normalized === 'yes' ||
    normalized === 'y' ||
    normalized === '1'
  );
};

/**
 * Parse processing status from API string.
 * The backend pipeline writes the current step name as the status
 * (e.g., "extract", "map", "evaluate", "save") and "Completed" when done.
 */
export const parseProcessingStatus = (status: string): ProcessingStatus => {
  const normalized = status.toLowerCase().trim();

  switch (normalized) {
    case 'pending':
    case 'queued':
      return 'pending';
    case 'processing':
    case 'in_progress':
    case 'running':
      return 'processing';
    case 'extract':
      return 'extract';
    case 'map':
    case 'mapping':
      return 'map';
    case 'evaluate':
    case 'evaluating':
      return 'evaluate';
    case 'save':
    case 'saving':
      return 'save';
    case 'completed':
    case 'complete':
    case 'success':
    case 'done':
      return 'completed';
    case 'failed':
    case 'error':
    case 'failure':
      return 'failed';
    default:
      return 'processing';
  }
};

/**
 * Generate a unique ID for extracted items
 */
const generateId = (prefix: string, index: number): string => {
  return `${prefix}-${String(index + 1).padStart(3, '0')}`;
};

/**
 * Create a placeholder citation from extracted data
 * In a real implementation, citations would come from the API
 */
const createPlaceholderCitation = (
  documentName: string,
  fieldName: string
): Citation => {
  return {
    id: `cit-${fieldName}-${Date.now()}`,
    documentId: 'doc-unknown',
    documentName: documentName,
    excerpt: 'No citation details are available.',
  };
};

/**
 * Mapping from ground risk field names to enhanced field_extractions keys
 */
const ENHANCED_FIELD_MAP: Record<string, string> = {
  max_dimensions: 'aircraft_dimension',
  max_speed_mps: 'max_speed',
  weight_over_250g: 'mass',
  controlled_ground_area: 'ground_area_control',
  max_population_density: 'population_density',
};

/**
 * Mapping from air risk API field names to enhanced field_extractions keys
 */
const AIR_ENHANCED_FIELD_MAP: Record<string, string> = {
  atypical_air_environment_only: 'atypical_air_environment',
  ifp_traffic_area: 'ifp_traffic',
  vfr_corridor_low_level_helicopters: 'vfr_corridor',
  flying_above_500ft: 'altitude',
  operation_type: 'operation_type',
};

/**
 * Create a citation from enhanced field extraction evidence
 */
const createEnhancedCitation = (
  documentName: string,
  fieldName: string,
  evidence: Record<string, unknown>
): Citation => {
  const evidenceType = evidence.type as string | undefined;
  const sourceLocation = (evidence.source_location as string) || undefined;
  const sourceLocations = evidence.source_locations as string[] | undefined;
  const sourceQuote = (evidence.source_quote as string) || undefined;
  const confidence = evidence.confidence as number | undefined;
  const supportingEvidence = evidence.supporting_evidence as string[] | undefined;
  const contradictingEvidence = evidence.contradicting_evidence as string[] | undefined;

  // Parse page number from source_location if available
  const locationStr = sourceLocation || (sourceLocations ? sourceLocations[0] : undefined);
  let pageNumber: number | undefined;
  let sectionReference: string | undefined;
  if (locationStr) {
    const pageMatch = locationStr.match(/Page\s+(\d+)/i);
    if (pageMatch) pageNumber = parseInt(pageMatch[1], 10);
    sectionReference = locationStr;
  }

  return {
    id: `cit-enhanced-${fieldName}-${Date.now()}`,
    documentId: 'doc-source',
    documentName,
    pageNumber,
    sectionReference,
    excerpt: sourceQuote || 'See source document for details.',
    highlightedText: sourceQuote || undefined,
    sourceLocation: locationStr,
    sourceQuote,
    evidenceType: evidenceType === 'direct' ? 'direct' : evidenceType === 'inferred' ? 'inferred' : undefined,
    evidenceConfidence: confidence,
    supportingEvidence,
    contradictingEvidence,
    assumptions: (evidence as Record<string, unknown>).assumptions
      ? ((evidence as Record<string, unknown>).assumptions as Array<Record<string, string>>).map(a => ({
          description: a.description || '',
          impactIfWrong: a.impact_if_wrong || '',
        }))
      : undefined,
  };
};

/**
 * Extract value and citation from enhanced field_extractions for a given field
 */
const getEnhancedFieldData = (
  enhanced: Record<string, unknown>,
  apiField: string,
  documentName: string
): { value: string | null; confidence: ConfidenceLevel; citation: Citation | null } => {
  const fieldExtractions = enhanced.field_extractions as Record<string, Record<string, unknown>> | undefined;
  if (!fieldExtractions) return { value: null, confidence: 'low', citation: null };

  const enhancedKey = ENHANCED_FIELD_MAP[apiField];
  if (!enhancedKey) return { value: null, confidence: 'low', citation: null };

  const fieldData = fieldExtractions[enhancedKey] as Record<string, unknown> | undefined;
  if (!fieldData) return { value: null, confidence: 'low', citation: null };

  // Extract the value — different fields store it differently
  let value: string | null = null;
  if (enhancedKey === 'aircraft_dimension') {
    const category = fieldData.category as string | undefined;
    const metres = fieldData.value_metres as number | undefined;
    value = category || (metres != null ? `${metres}m` : null);
  } else if (enhancedKey === 'max_speed') {
    const ms = fieldData.value_ms as number | undefined;
    value = ms != null ? `${ms}` : null;
  } else if (enhancedKey === 'mass') {
    const above250g = fieldData.above_250g as boolean | undefined;
    value = above250g != null ? (above250g ? 'Yes' : 'No') : null;
  } else if (enhancedKey === 'ground_area_control' || enhancedKey === 'population_density') {
    value = (fieldData.value as string) || null;
  }

  // Extract confidence from evidence
  const evidence = fieldData.evidence as Record<string, unknown> | undefined;
  let confidence: ConfidenceLevel = 'medium';
  if (evidence?.confidence != null) {
    confidence = confidenceToLevel(evidence.confidence as number);
  }

  // Build citation from evidence
  let citation: Citation | null = null;
  if (evidence) {
    citation = createEnhancedCitation(documentName, apiField, evidence);
  }

  return { value, confidence, citation };
};

/**
 * Extract value and citation from enhanced field_extractions for an air risk field.
 * Air risk enhanced fields have different value structures than ground risk.
 */
const getAirEnhancedFieldData = (
  enhanced: Record<string, unknown>,
  apiField: string,
  isBoolean: boolean,
  documentName: string
): { value: string | boolean | null; confidence: ConfidenceLevel; citation: Citation | null } => {
  const fieldExtractions = enhanced.field_extractions as Record<string, Record<string, unknown>> | undefined;
  if (!fieldExtractions) return { value: null, confidence: 'low', citation: null };

  const enhancedKey = AIR_ENHANCED_FIELD_MAP[apiField];
  if (!enhancedKey) return { value: null, confidence: 'low', citation: null };

  const fieldData = fieldExtractions[enhancedKey] as Record<string, unknown> | undefined;
  if (!fieldData) return { value: null, confidence: 'low', citation: null };

  // Extract value — different air risk fields store values differently
  let value: string | boolean | null = null;
  if (enhancedKey === 'altitude') {
    // altitude has above_500ft boolean
    const above500 = fieldData.above_500ft as boolean | undefined;
    if (above500 != null) {
      value = isBoolean ? above500 : (above500 ? 'Yes' : 'No');
    }
  } else if (enhancedKey === 'operation_type' || enhancedKey === 'airspace_class') {
    const strVal = fieldData.value as string | undefined;
    if (strVal) value = strVal;
  } else {
    // Boolean fields (atypical_air_environment, ifp_traffic, vfr_corridor)
    const strVal = fieldData.value as string | boolean | undefined;
    if (strVal != null) {
      if (isBoolean) {
        value = typeof strVal === 'boolean' ? strVal : parseBoolean(String(strVal));
      } else {
        value = String(strVal);
      }
    }
  }

  // Extract confidence from evidence
  const evidence = fieldData.evidence as Record<string, unknown> | undefined;
  let confidence: ConfidenceLevel = 'medium';
  if (evidence?.confidence != null) {
    confidence = confidenceToLevel(evidence.confidence as number);
  }

  // Build citation from evidence
  let citation: Citation | null = null;
  if (evidence) {
    citation = createEnhancedCitation(documentName, apiField, evidence);
  }

  return { value, confidence, citation };
};

// ============================================================================
// Field Classification Functions
// ============================================================================

/**
 * Check if a field name corresponds to a ground risk answer field
 */
export const isGroundRiskField = (fieldName: string): boolean => {
  const normalized = fieldName.toLowerCase().replace(/\s+/g, '_');
  return normalized in GROUND_RISK_FIELD_MAP;
};

/**
 * Check if a field name corresponds to an air risk answer field
 */
export const isAirRiskField = (fieldName: string): boolean => {
  const normalized = fieldName.toLowerCase().replace(/\s+/g, '_');
  return normalized in AIR_RISK_FIELD_MAP;
};

/**
 * Check if a field name corresponds to a ground risk mitigation
 */
export const isGroundMitigation = (fieldName: string): boolean => {
  const normalized = fieldName.toLowerCase().replace(/\s+/g, '_');
  return normalized in GROUND_MITIGATION_CODE_MAP;
};

/**
 * Check if a field name corresponds to an air risk mitigation
 */
export const isAirMitigation = (fieldName: string): boolean => {
  const normalized = fieldName.toLowerCase().replace(/\s+/g, '_');
  return normalized in AIR_MITIGATION_CODE_MAP;
};

// ============================================================================
// Parsing Functions
// ============================================================================

/**
 * Parse an extracted comparison item into a ground risk answer
 */
export const parseGroundRiskAnswer = (
  item: ApiExtractedComparisonItem,
  index: number,
  documentName: string
): GroundRiskAnswer | null => {
  const normalized = item.Field?.toLowerCase().replace(/\s+/g, '_') ?? '';
  const field = GROUND_RISK_FIELD_MAP[normalized];

  if (!field) {
    return null;
  }

  return {
    id: generateId('gra', index),
    field,
    extractedValue: item.Extracted as string,
    confidence: confidenceToLevel(item.Confidence ?? ''),
    citations: [createPlaceholderCitation(documentName, item.Field ?? '')],
  };
};

/**
 * Parse an extracted comparison item into an air risk answer
 */
export const parseAirRiskAnswer = (
  item: ApiExtractedComparisonItem,
  index: number,
  documentName: string
): AirRiskAnswer | null => {
  const normalized = item.Field?.toLowerCase().replace(/\s+/g, '_') ?? '';
  const field = AIR_RISK_FIELD_MAP[normalized];

  if (!field) {
    return null;
  }

  // Determine if the value should be boolean or string
  let extractedValue: string | boolean = item.Extracted as string;

  // Boolean fields
  if (
    field === 'aae_only' ||
    field === 'known_ifp_area' ||
    field === 'vfr_corridor_area' ||
    field === 'above_500ft_agl'
  ) {
    extractedValue = parseBoolean(item.Extracted as string);
  }

  return {
    id: generateId('ara', index),
    field,
    extractedValue,
    confidence: confidenceToLevel(item.Confidence ?? ''),
    citations: [createPlaceholderCitation(documentName, item.Field ?? '')],
  };
};

/**
 * Parse an extracted comparison item into a ground risk mitigation
 */
export const parseGroundRiskMitigation = (
  item: ApiExtractedComparisonItem,
  index: number,
  documentName: string
): GroundRiskMitigation | null => {
  const normalized = item.Field?.toLowerCase().replace(/\s+/g, '_') ?? '';
  const code = GROUND_MITIGATION_CODE_MAP[normalized];

  if (!code) {
    return null;
  }

  return {
    id: generateId('grm', index),
    code,
    name: GROUND_MITIGATION_NAMES[code],
    selectedLevel: parseMitigationLevel(item.Extracted as string),
    commentary: '', // Commentary would come from additional API fields
    evidence: item.Extracted as string,
    citations: [createPlaceholderCitation(documentName, item.Field ?? '')],
  };
};

/**
 * Parse an extracted comparison item into an air risk mitigation
 */
export const parseAirRiskMitigation = (
  item: ApiExtractedComparisonItem,
  index: number,
  documentName: string
): AirRiskMitigation | null => {
  const normalized = item.Field?.toLowerCase().replace(/\s+/g, '_') ?? '';
  const code = AIR_MITIGATION_CODE_MAP[normalized];

  if (!code) {
    return null;
  }

  return {
    id: generateId('arm', index),
    code,
    name: AIR_MITIGATION_NAMES[code],
    selectedValue: parseBoolean(item.Extracted as string),
    commentary: '', // Commentary would come from additional API fields
    evidence: item.Extracted as string,
    citations: [createPlaceholderCitation(documentName, item.Field ?? '')],
  };
};

// ============================================================================
// Result Object Parsing Functions
// ============================================================================

/**
 * Interface for the ground_risk_class object from API result
 */
interface ApiGroundRiskClass {
  max_dimensions?: string;
  max_speed_mps?: string;
  weight_over_250g?: string;
  controlled_ground_area?: string;
  max_population_density?: string;
  ground_risk_class_result?: string;
  grc_determination_notes?: string;
  enhanced?: Record<string, unknown>;
}

/**
 * Interface for the ground_risk_mitigation object from API result
 */
interface ApiGroundRiskMitigation {
  m1a_sheltering?: string | Record<string, unknown>;
  m1b_operational_restrictions?: string | Record<string, unknown>;
  m1c_ground_observation?: string | Record<string, unknown>;
  m2_reducing_impact_dynamics?: string | Record<string, unknown>;
  mitigation_notes?: string;
  enhanced?: Record<string, unknown>;
}


/**
 * Interface for the air_risk_class object from API result
 */
interface ApiAirRiskClass {
  atypical_air_environment_only?: string;
  airspace_class?: string;
  ifp_traffic_area?: string;
  vfr_corridor_low_level_helicopters?: string;
  flying_above_500ft?: string;
  operation_type?: string;
  claim_lower_arc?: string;
  initial_arc_result?: string;
  claimed_arc_result?: string;
  arc_determination_notes?: string;
  enhanced?: Record<string, unknown>;
}

/**
 * Interface for a strategic mitigation from API result
 */
interface ApiStrategicMitigation {
  mitigation_code?: string;
  mitigation_name?: string;
  selected?: string;
  details?: string;
  enhanced?: Record<string, unknown>;
}

/**
 * Interface for aircraft specification from API result
 */
interface AircraftSpecification {
  manufacturer?: string;
  model?: string;
  serial_number?: string;
  aircraft_type?: string;
  weight_grams?: number;
  max_dimension_metres?: number;
  max_speed_metres_per_second?: number;
  max_altitude_metres?: number;
  max_endurance_minutes?: number;
  propulsion_type?: string;
  power_source?: string;
  max_wind_speed_kph?: number;
  min_temperature_celsius?: number;
  max_temperature_celsius?: number;
  max_operating_range_km?: number;
  weather_limitations?: string;
}

/**
 * Interface for operating area from API result
 */
interface ApiOperatingArea {
  area_of_operation_uk?: string;
  geographic_description?: string;
  typical_operation_types?: string;
  latitude_center?: number;
  longitude_center?: number;
  operational_volume_description?: string;
  airspace_restrictions?: string;
}

/**
 * Parse ground risk answers from the result.ground_risk_class object
 * Falls back to aircraft_specifications if ground_risk_class is null
 * Always returns all expected fields, marking missing ones as "Unknown"
 */
export const parseGroundRiskAnswersFromResult = (
  groundRiskClass: ApiGroundRiskClass | null | undefined,
  documentName: string,
  aircraftSpecs?: AircraftSpecification[] | null
): GroundRiskAnswer[] => {
  const answers: GroundRiskAnswer[] = [];

  // Define all expected ground risk fields with their display info
  const allFields: Array<{
    field: GroundRiskField;
    apiField: keyof ApiGroundRiskClass;
    displayName: string;
  }> = [
    { field: 'max_dimensions', apiField: 'max_dimensions', displayName: 'Maximum Dimensions' },
    { field: 'max_speed_mps', apiField: 'max_speed_mps', displayName: 'Maximum Speed (m/s)' },
    { field: 'weight_over_250g', apiField: 'weight_over_250g', displayName: 'Weight Over 250g' },
    { field: 'controlled_ground_area_only', apiField: 'controlled_ground_area', displayName: 'Controlled Ground Area Only' },
    { field: 'max_population_density', apiField: 'max_population_density', displayName: 'Maximum Population Density' },
  ];

  // Try to get values from aircraft_specifications if ground_risk_class is null
  let fallbackMaxDimension: string | null = null;
  let fallbackMaxSpeed: string | null = null;
  let fallbackWeightOver250g: string | null = null;

  if (aircraftSpecs && aircraftSpecs.length > 0) {
    // Use the first aircraft's specs as fallback, or find the largest/heaviest
    const maxDimension = Math.max(...aircraftSpecs.map(a => a.max_dimension_metres || 0));
    const maxSpeed = Math.max(...aircraftSpecs.map(a => a.max_speed_metres_per_second || 0));
    const maxWeight = Math.max(...aircraftSpecs.map(a => a.weight_grams || 0));

    if (maxDimension > 0) {
      fallbackMaxDimension = `${maxDimension}m`;
    }
    if (maxSpeed > 0) {
      fallbackMaxSpeed = `${maxSpeed} m/s`;
    }
    if (maxWeight > 0) {
      fallbackWeightOver250g = maxWeight > 250 ? 'Yes' : 'No';
    }
  }

  allFields.forEach((mapping, index) => {
    let value: string | null = null;
    let confidence: ConfidenceLevel = 'low';
    let citations: Citation[] = [];

    // First try to get from enhanced field_extractions
    if (groundRiskClass?.enhanced) {
      const enhancedData = getEnhancedFieldData(
        groundRiskClass.enhanced as Record<string, unknown>,
        mapping.apiField,
        documentName
      );
      if (enhancedData.value) {
        value = enhancedData.value;
        confidence = enhancedData.confidence;
        if (enhancedData.citation) {
          citations = [enhancedData.citation];
        }
      }
    }

    // Fall back to top-level ground_risk_class fields
    if (!value && groundRiskClass) {
      const grcValue = groundRiskClass[mapping.apiField];
      if (grcValue && typeof grcValue === 'string') {
        value = grcValue;
        const enhanced = groundRiskClass.enhanced as Record<string, unknown> | undefined;
        const confValue = enhanced?.confidence as string | number | undefined;
        confidence = confValue ? confidenceToLevel(confValue) : 'medium';
      }
    }

    // If no value, try fallback from aircraft_specifications
    if (!value) {
      if (mapping.field === 'max_dimensions' && fallbackMaxDimension) {
        value = fallbackMaxDimension;
        confidence = 'medium';
      } else if (mapping.field === 'max_speed_mps' && fallbackMaxSpeed) {
        value = fallbackMaxSpeed;
        confidence = 'medium';
      } else if (mapping.field === 'weight_over_250g' && fallbackWeightOver250g) {
        value = fallbackWeightOver250g;
        confidence = 'medium';
      }
    }

    // Use placeholder citation if none from enhanced
    if (citations.length === 0) {
      citations = [createPlaceholderCitation(documentName, mapping.apiField)];
    }

    // Always add the field, with "Unknown" if no value found
    answers.push({
      id: generateId('gra', index),
      field: mapping.field,
      extractedValue: value || 'Unknown',
      confidence: value ? confidence : 'low',
      citations,
    });
  });

  return answers;
};

/**
 * Parse ground risk mitigations from the result.ground_risk_mitigation object
 * Always returns all 4 mitigations (M1A, M1B, M1C, M2), marking missing ones as "Unknown"
 */
export const parseGroundRiskMitigationsFromResult = (
  groundRiskMitigation: ApiGroundRiskMitigation | null | undefined,
  documentName: string
): GroundRiskMitigation[] => {
  const mitigations: GroundRiskMitigation[] = [];

  const enhancedBlock = groundRiskMitigation?.enhanced as Record<string, unknown> | undefined;
  const fieldExtractions = enhancedBlock?.field_extractions as Record<string, Record<string, unknown>> | undefined;

  const mitigationMappings: Array<{
    apiField: keyof ApiGroundRiskMitigation;
    code: GroundRiskMitigationCode;
  }> = [
    { apiField: 'm1a_sheltering', code: 'M1A' },
    { apiField: 'm1b_operational_restrictions', code: 'M1B' },
    { apiField: 'm1c_ground_observation', code: 'M1C' },
    { apiField: 'm2_reducing_impact_dynamics', code: 'M2' },
  ];

  mitigationMappings.forEach((mapping, index) => {
    const rawField = groundRiskMitigation?.[mapping.apiField];

    // Case 1: field value is an object (enhanced format directly on the field)
    if (rawField && typeof rawField === 'object') {
      const fieldObj = rawField as Record<string, unknown>;
      const enhancedValue = fieldObj.value as string | undefined;
      const evidence = fieldObj.evidence as Record<string, unknown> | undefined;

      const selectedLevel = enhancedValue ? parseMitigationLevel(enhancedValue) : 'not_applicable';
      const commentary = (evidence?.robustness_justification as string) || '';

      let evidenceStr = enhancedValue || 'Unknown';
      if (evidence) {
        const eType = (evidence.type as string) || '';
        const eConf = evidence.confidence as number | undefined;
        const parts: string[] = [];
        if (eType) parts.push(eType.charAt(0).toUpperCase() + eType.slice(1));
        if (eConf != null) parts.push(`${Math.round(eConf * 100)}% confidence`);
        if (parts.length > 0) evidenceStr = parts.join(' — ');
      }

      const citation = evidence
        ? createEnhancedCitation(documentName, mapping.apiField as string, evidence)
        : createPlaceholderCitation(documentName, mapping.apiField as string);

      const eConfidence = evidence?.confidence as number | undefined;
      const topConf = fieldObj.confidence as number | undefined;
      const finalConfidence = eConfidence ?? topConf;
      const confidence = finalConfidence != null ? confidenceToLevel(finalConfidence) : undefined;

      mitigations.push({
        id: generateId('grm', index),
        code: mapping.code,
        name: GROUND_MITIGATION_NAMES[mapping.code],
        selectedLevel,
        confidence,
        commentary,
        evidence: evidenceStr,
        citations: [citation],
      });
      return;
    }

    // Case 2: check enhanced.field_extractions[apiField]
    const enhancedField = fieldExtractions?.[mapping.apiField] as Record<string, unknown> | undefined;
    if (enhancedField) {
      const enhancedValue = enhancedField.value as string | undefined;
      const evidence = enhancedField.evidence as Record<string, unknown> | undefined;
      const topLevelConfidence = enhancedField.confidence as number | undefined;

      const selectedLevel = enhancedValue ? parseMitigationLevel(enhancedValue) : 'not_applicable';
      const commentary = (evidence?.robustness_justification as string) || '';

      let evidenceStr = enhancedValue || 'Unknown';
      if (evidence) {
        const eType = (evidence.type as string) || '';
        const eConf = evidence.confidence as number | undefined;
        const parts: string[] = [];
        if (eType) parts.push(eType.charAt(0).toUpperCase() + eType.slice(1));
        if (eConf != null) parts.push(`${Math.round(eConf * 100)}% confidence`);
        if (parts.length > 0) evidenceStr = parts.join(' — ');
      }

      const citation = evidence
        ? createEnhancedCitation(documentName, mapping.apiField as string, evidence)
        : createPlaceholderCitation(documentName, mapping.apiField as string);

      const eConfidence = evidence?.confidence as number | undefined;
      const finalConfidence = eConfidence ?? topLevelConfidence;
      const confidence = finalConfidence != null ? confidenceToLevel(finalConfidence) : undefined;

      mitigations.push({
        id: generateId('grm', index),
        code: mapping.code,
        name: GROUND_MITIGATION_NAMES[mapping.code],
        selectedLevel,
        confidence,
        commentary,
        evidence: evidenceStr,
        citations: [citation],
      });
      return;
    }

    // Case 3: plain string value (old format)
    let value: string | null = null;
    if (rawField && typeof rawField === 'string') {
      value = rawField;
    }

    mitigations.push({
      id: generateId('grm', index),
      code: mapping.code,
      name: GROUND_MITIGATION_NAMES[mapping.code],
      selectedLevel: value ? parseMitigationLevel(value) : 'not_applicable',
      commentary: groundRiskMitigation?.mitigation_notes || '',
      evidence: value || 'Unknown',
      citations: [createPlaceholderCitation(documentName, mapping.apiField as string)],
    });
  });

  return mitigations;
};




/**
 * Parse air risk answers from the result.air_risk_class object
 * Falls back to operating_area for operation_type if air_risk_class is null
 * Always returns all expected fields, marking missing ones as "Unknown"
 */
export const parseAirRiskAnswersFromResult = (
  airRiskClass: ApiAirRiskClass | null | undefined,
  documentName: string,
  operatingArea?: ApiOperatingArea | null
): AirRiskAnswer[] => {
  const answers: AirRiskAnswer[] = [];

  // Define all expected air risk fields with their display info
  const fieldMappings: Array<{
    apiField: keyof ApiAirRiskClass;
    field: AirRiskField;
    isBoolean: boolean;
    displayName: string;
  }> = [
    { apiField: 'atypical_air_environment_only', field: 'aae_only', isBoolean: true, displayName: 'Atypical Air Environment Only' },
    { apiField: 'ifp_traffic_area', field: 'known_ifp_area', isBoolean: true, displayName: 'Known IFP Traffic Area' },
    { apiField: 'vfr_corridor_low_level_helicopters', field: 'vfr_corridor_area', isBoolean: true, displayName: 'VFR Corridor / Low Level Helicopters' },
    { apiField: 'flying_above_500ft', field: 'above_500ft_agl', isBoolean: true, displayName: 'Flying Above 500ft AGL' },
    { apiField: 'operation_type', field: 'operation_type', isBoolean: false, displayName: 'Operation Type' },
  ];

  // Try to extract operation_type from operating_area.operational_volume_description
  let fallbackOperationType: string | null = null;
  if (operatingArea?.operational_volume_description) {
    const desc = operatingArea.operational_volume_description.toUpperCase();
    if (desc.includes('BVLOS') && !desc.includes('VISUAL')) {
      fallbackOperationType = 'BVLOS';
    } else if (desc.includes('BVLOS') && desc.includes('VISUAL')) {
      fallbackOperationType = 'BVLOS with visual mitigations';
    } else if (desc.includes('VLOS')) {
      fallbackOperationType = 'VLOS';
    }
  }

  fieldMappings.forEach((mapping, index) => {
    let value: string | boolean | null = null;
    let confidence: ConfidenceLevel = 'low';
    let citations: Citation[] = [];

    // First try to get from enhanced field_extractions
    if (airRiskClass?.enhanced) {
      const enhancedData = getAirEnhancedFieldData(
        airRiskClass.enhanced as Record<string, unknown>,
        mapping.apiField,
        mapping.isBoolean,
        documentName
      );
      if (enhancedData.value != null) {
        value = enhancedData.value;
        confidence = enhancedData.confidence;
        if (enhancedData.citation) {
          citations = [enhancedData.citation];
        }
      }
    }

    // Fall back to top-level air_risk_class fields
    if (value === null && airRiskClass) {
      const arcValue = airRiskClass[mapping.apiField];
      if (arcValue && typeof arcValue === 'string') {
        value = mapping.isBoolean ? parseBoolean(arcValue) : arcValue;
        const enhanced = airRiskClass.enhanced as Record<string, unknown> | undefined;
        const confValue = enhanced?.confidence as string | number | undefined;
        confidence = confValue ? confidenceToLevel(confValue) : 'medium';
      }
    }

    // If no value for operation_type, try fallback from operating_area
    if (value === null && mapping.field === 'operation_type' && fallbackOperationType) {
      value = fallbackOperationType;
      confidence = 'medium';
    }

    // Use placeholder citation if none from enhanced
    if (citations.length === 0) {
      citations = [createPlaceholderCitation(documentName, mapping.apiField)];
    }

    // Always add the field, with "Unknown" if no value found
    answers.push({
      id: generateId('ara', index),
      field: mapping.field,
      extractedValue: value !== null ? value : (mapping.isBoolean ? false : 'Unknown'),
      confidence: value !== null ? confidence : 'low',
      citations,
    });
  });

  return answers;
};

/**
 * Parse air risk mitigations from the result.strategic_mitigations array
 * Always returns all 10 strategic mitigations (SM1-SM10), marking missing ones as "Unknown"
 */
export const parseAirRiskMitigationsFromResult = (
  strategicMitigations: ApiStrategicMitigation[] | null | undefined,
  documentName: string
): AirRiskMitigation[] => {
  const mitigations: AirRiskMitigation[] = [];

  const allCodes: AirRiskMitigationCode[] = ['SM1', 'SM2', 'SM3', 'SM4', 'SM5', 'SM6', 'SM7', 'SM8', 'SM9', 'SM10'];

  const existingMitigations = new Map<string, ApiStrategicMitigation>();
  if (strategicMitigations && Array.isArray(strategicMitigations)) {
    for (const sm of strategicMitigations) {
      const code = sm.mitigation_code?.toUpperCase();
      if (code) {
        existingMitigations.set(code, sm);
      }
    }
  }

  allCodes.forEach((code, index) => {
    const existing = existingMitigations.get(code);

    if (existing) {
      // Check for enhanced data on this mitigation
      const enhanced = existing.enhanced as Record<string, unknown> | undefined;
      const evidence = enhanced?.evidence as Record<string, unknown> | undefined;
      const topLevelConfidence = enhanced?.confidence as number | undefined;

      // Build evidence string from enhanced data if available
      let evidenceStr = existing.details || '';
      let confidence: ConfidenceLevel | undefined;

      if (evidence || topLevelConfidence != null) {
        const eType = (evidence?.type as string) || '';
        const eConf = (evidence?.confidence as number | undefined) ?? topLevelConfidence;

        // Format evidence string as type + confidence
        const parts: string[] = [];
        if (eType) parts.push(eType.charAt(0).toUpperCase() + eType.slice(1));
        if (eConf != null) parts.push(`${Math.round(eConf * 100)}% confidence`);
        if (parts.length > 0) evidenceStr = parts.join(' — ');

        // Extract confidence level
        if (eConf != null) {
          confidence = confidenceToLevel(eConf);
        }
      }

      // Build citation from enhanced evidence or use placeholder
      const citation = evidence
        ? createEnhancedCitation(documentName, existing.mitigation_code || code, evidence)
        : createPlaceholderCitation(documentName, existing.mitigation_code || code);

      mitigations.push({
        id: generateId('arm', index),
        code,
        name: existing.mitigation_name || AIR_MITIGATION_NAMES[code],
        selectedValue: parseBoolean(existing.selected || 'No'),
        confidence,
        commentary: existing.details || '',
        evidence: evidenceStr,
        citations: [citation],
      });
    } else {
      mitigations.push({
        id: generateId('arm', index),
        code,
        name: AIR_MITIGATION_NAMES[code],
        selectedValue: false,
        commentary: '',
        evidence: 'Unknown',
        citations: [createPlaceholderCitation(documentName, code)],
      });
    }
  });

  return mitigations;
};


/**
 * Parse extraction results from the API result object
 * This handles the nested structure: result.ground_risk_class, result.ground_risk_mitigation, etc.
 * Also extracts fallback data from aircraft_specifications and operating_area
 */
export const parseResultObject = (
  result: Record<string, unknown> | null | undefined,
  documentName: string
): {
  groundRiskAnswers: GroundRiskAnswer[];
  groundRiskMitigations: GroundRiskMitigation[];
  airRiskAnswers: AirRiskAnswer[];
  airRiskMitigations: AirRiskMitigation[];
} => {
  if (!result) {
    // Even with no result, return all fields with "Unknown" values
    return {
      groundRiskAnswers: parseGroundRiskAnswersFromResult(null, documentName, null),
      groundRiskMitigations: parseGroundRiskMitigationsFromResult(null, documentName),
      airRiskAnswers: parseAirRiskAnswersFromResult(null, documentName, null),
      airRiskMitigations: parseAirRiskMitigationsFromResult(null, documentName),
    };
  }

  // Extract aircraft_specifications and operating_area for fallback data
  const aircraftSpecs = result.aircraft_specifications as AircraftSpecification[] | undefined;
  const operatingArea = result.operating_area as ApiOperatingArea | undefined;

  return {
    groundRiskAnswers: parseGroundRiskAnswersFromResult(
      result.ground_risk_class as ApiGroundRiskClass | undefined,
      documentName,
      aircraftSpecs
    ),
    groundRiskMitigations: parseGroundRiskMitigationsFromResult(
      result.ground_risk_mitigation as ApiGroundRiskMitigation | undefined,
      documentName
    ),
    airRiskAnswers: parseAirRiskAnswersFromResult(
      result.air_risk_class as ApiAirRiskClass | undefined,
      documentName,
      operatingArea
    ),
    airRiskMitigations: parseAirRiskMitigationsFromResult(
      result.strategic_mitigations as ApiStrategicMitigation[] | undefined,
      documentName
    ),
  };
};

// ============================================================================
// Main Parser Function
// ============================================================================

/**
 * Parse a ContentProcessorResponse from the API into ExtractionResults
 *
 * This function handles:
 * - Field mapping from snake_case to camelCase
 * - Classification of extracted items into ground/air risk categories
 * - Conversion of confidence scores to levels
 * - Status normalization
 *
 * @param response - The raw API response
 * @returns Parsed ExtractionResults for frontend use
 */
export const parseContentProcessorResponse = (
  response: ContentProcessorResponse
): ExtractionResults => {
  const documentName = response.processed_file_name;

  // Classify and parse extracted comparison data
  const groundRiskAnswers: GroundRiskAnswer[] = [];
  const groundRiskMitigations: GroundRiskMitigation[] = [];
  const airRiskAnswers: AirRiskAnswer[] = [];
  const airRiskMitigations: AirRiskMitigation[] = [];

  // Track indices for ID generation
  let graIndex = 0;
  let grmIndex = 0;
  let araIndex = 0;
  let armIndex = 0;

  // Process each extracted comparison item
  for (const item of response.extracted_comparison_data) {
    const fieldNormalized = item.Field?.toLowerCase().replace(/\s+/g, '_') ?? '';

    // Try to classify the field
    if (isGroundRiskField(fieldNormalized)) {
      const answer = parseGroundRiskAnswer(item, graIndex, documentName);
      if (answer) {
        groundRiskAnswers.push(answer);
        graIndex++;
      }
    } else if (isAirRiskField(fieldNormalized)) {
      const answer = parseAirRiskAnswer(item, araIndex, documentName);
      if (answer) {
        airRiskAnswers.push(answer);
        araIndex++;
      }
    } else if (isGroundMitigation(fieldNormalized)) {
      const mitigation = parseGroundRiskMitigation(item, grmIndex, documentName);
      if (mitigation) {
        groundRiskMitigations.push(mitigation);
        grmIndex++;
      }
    } else if (isAirMitigation(fieldNormalized)) {
      const mitigation = parseAirRiskMitigation(item, armIndex, documentName);
      if (mitigation) {
        airRiskMitigations.push(mitigation);
        armIndex++;
      }
    }
    // Unrecognized fields are silently ignored
  }

  // Build the ExtractionResults object with camelCase field names
  return {
    processId: response.process_id,
    processedFileName: response.processed_file_name,
    processedFileMimeType: response.processed_file_mime_type,
    processedTime: response.processed_time,
    importedTime: response.imported_time,
    lastModifiedTime: response.last_modified_time,
    lastModifiedBy: response.last_modified_by,
    status: parseProcessingStatus(response.status),
    entityScore: response.entity_score,
    minExtractedEntityScore: response.min_extracted_entity_score,
    schemaScore: response.schema_score,
    result: response.result,
    confidence: response.confidence,
    targetSchema: response.target_schema,
    promptTokens: response.prompt_tokens,
    completionTokens: response.completion_tokens,
    processOutput: response.process_output,
    groundRiskAnswers,
    groundRiskMitigations,
    airRiskAnswers,
    airRiskMitigations,
  };
};

/**
 * Validate that a ContentProcessorResponse has the required fields
 *
 * @param response - The response to validate
 * @returns True if the response is valid, false otherwise
 */
export const isValidContentProcessorResponse = (
  response: unknown
): response is ContentProcessorResponse => {
  if (!response || typeof response !== 'object') {
    return false;
  }

  const r = response as Record<string, unknown>;

  // Check required string fields
  const requiredStringFields = [
    'process_id',
    'processed_file_name',
    'processed_file_mime_type',
    'processed_time',
    'imported_time',
    'last_modified_time',
    'last_modified_by',
    'status',
    'result',
    'target_schema',
    'process_output',
  ];

  for (const field of requiredStringFields) {
    if (typeof r[field] !== 'string') {
      return false;
    }
  }

  // Check required number fields
  const requiredNumberFields = [
    'entity_score',
    'min_extracted_entity_score',
    'schema_score',
    'confidence',
    'prompt_tokens',
    'completion_tokens',
  ];

  for (const field of requiredNumberFields) {
    if (typeof r[field] !== 'number') {
      return false;
    }
  }

  // Check extracted_comparison_data is an array
  if (!Array.isArray(r.extracted_comparison_data)) {
    return false;
  }

  return true;
};

/**
 * Safely parse a ContentProcessorResponse with validation
 *
 * @param response - The raw response to parse
 * @returns Parsed ExtractionResults or null if invalid
 */
export const safeParseContentProcessorResponse = (
  response: unknown
): ExtractionResults | null => {
  if (!isValidContentProcessorResponse(response)) {
    return null;
  }

  try {
    return parseContentProcessorResponse(response);
  } catch {
    return null;
  }
};

// ============================================================================
// ContentProcessorAPI Response Parsers (Requirements 4.1-4.5)
// ============================================================================

/**
 * Parse a StepOutput from the API into ProcessStepOutput (camelCase).
 *
 * Transforms snake_case backend fields to camelCase frontend fields.
 * Handles null/undefined fields with appropriate defaults.
 *
 * @param output - The raw StepOutput from the API
 * @returns Parsed ProcessStepOutput for frontend use
 *
 * Requirements: 4.1, 4.5
 */
export const parseStepOutput = (output: StepOutput): ProcessStepOutput => {
  return {
    stepName: output.step_name ?? '',
    processedTime: output.processed_time ?? null,
    stepResult: output.step_result ?? null,
  };
};

/**
 * Parse an ExtractedComparisonItem from the API into ExtractionComparisonItem (camelCase).
 *
 * Transforms PascalCase backend fields to camelCase frontend fields.
 * Handles null/undefined fields with appropriate defaults.
 *
 * @param item - The raw ExtractedComparisonItem from the API
 * @returns Parsed ExtractionComparisonItem for frontend use
 *
 * Requirements: 4.2, 4.5
 */
export const parseExtractionComparisonItem = (
  item: ApiExtractedComparisonItem
): ExtractionComparisonItem => {
  return {
    field: item.Field ?? null,
    extracted: item.Extracted ?? null,
    confidence: item.Confidence ?? null,
    isAboveThreshold: item.IsAboveThreshold ?? null,
  };
};

/**
 * Parse a SchemaResponse from the API into Schema (camelCase).
 *
 * Transforms PascalCase backend fields to camelCase frontend fields.
 * Handles null Created_On and Updated_On fields.
 *
 * @param response - The raw SchemaResponse from the API
 * @returns Parsed Schema for frontend use
 *
 * Requirements: 4.4, 4.5
 */
export const parseSchema = (response: SchemaResponse): Schema => {
  return {
    id: response.Id ?? '',
    className: response.ClassName ?? '',
    description: response.Description ?? '',
    fileName: response.FileName ?? '',
    contentType: response.ContentType ?? '',
    createdOn: response.Created_On ?? null,
    updatedOn: response.Updated_On ?? null,
  };
};

/**
 * Parse a ContentProcessResponse from the API into ProcessedDocument (camelCase).
 *
 * This function handles:
 * - Field mapping from snake_case to camelCase
 * - Parsing nested target_schema using parseSchema
 * - Parsing process_output array using parseStepOutput
 * - Parsing extracted_comparison_data using parseExtractionComparisonItem
 * - Null/undefined field handling with appropriate defaults
 *
 * Default values for null fields (Requirement 4.5):
 * - String fields: null (keep as null)
 * - Number fields: 0
 * - Array fields: []
 * - Object fields: null
 *
 * @param response - The raw ContentProcessResponse from the API
 * @returns Parsed ProcessedDocument for frontend use
 *
 * Requirements: 4.1, 4.2, 4.3, 4.5
 */
export const parseContentProcessResponse = (
  response: ContentProcessResponse
): ProcessedDocument => {
  // Parse nested target_schema if present
  const targetSchema = response.target_schema
    ? parseSchema(response.target_schema)
    : null;

  // Parse process_output array with defaults for null/undefined
  const processOutput = Array.isArray(response.process_output)
    ? response.process_output.map(parseStepOutput)
    : [];

  // Parse extracted_comparison_data array with defaults for null/undefined
  const extractedComparisonData = Array.isArray(response.extracted_comparison_data)
    ? response.extracted_comparison_data.map(parseExtractionComparisonItem)
    : null;

  return {
    processId: response.process_id ?? '',
    processedFileName: response.processed_file_name ?? null,
    processedFileMimeType: response.processed_file_mime_type ?? null,
    processedTime: response.processed_time ?? null,
    importedTime: response.imported_time ?? '',
    lastModifiedTime: response.last_modified_time ?? '',
    lastModifiedBy: response.last_modified_by ?? null,
    status: parseProcessingStatus(response.status ?? ''),
    entityScore: response.entity_score ?? 0,
    minExtractedEntityScore: response.min_extracted_entity_score ?? 0,
    schemaScore: response.schema_score ?? 0,
    result: response.result ?? null,
    confidence: response.confidence ?? null,
    targetSchema,
    promptTokens: response.prompt_tokens ?? 0,
    completionTokens: response.completion_tokens ?? 0,
    processOutput,
    extractedComparisonData,
    comment: response.comment ?? null,
  };
};

/**
 * Parse a PaginatedApiResponse from the API into PaginatedResponse<T>.
 *
 * Transforms pagination metadata from snake_case to camelCase and
 * applies the provided itemParser to each item in the items array.
 *
 * @param response - The raw PaginatedApiResponse from the API
 * @param itemParser - Function to parse each item in the items array
 * @returns Parsed PaginatedResponse for frontend use
 *
 * Requirements: 4.3, 4.5
 */
export const parsePaginatedResponse = <T>(
  response: PaginatedApiResponse,
  itemParser: (item: ContentProcessResponse) => T
): PaginatedResponse<T> => {
  // Parse items array with defaults for null/undefined
  const items = Array.isArray(response.items)
    ? response.items.map(itemParser)
    : [];

  return {
    totalCount: response.total_count ?? 0,
    totalPages: response.total_pages ?? 0,
    currentPage: response.current_page ?? 0,
    pageSize: response.page_size ?? 0,
    items,
  };
};

/**
 * Status code to ProcessingStatus mapping.
 *
 * Based on ContentProcessorAPI status endpoint behavior:
 * - 200: processing (document is still being processed)
 * - 302: completed (processing finished, redirect to results)
 * - 404: not_found (process ID not found)
 * - 500: error (server error during processing)
 */
type StatusCodeMapping = {
  status: ProcessingStatus;
  message: string;
};

const STATUS_CODE_MAP: Record<number, StatusCodeMapping> = {
  200: { status: 'processing', message: 'Document is being processed' },
  302: { status: 'completed', message: 'Processing completed successfully' },
  404: { status: 'failed', message: 'Process not found' },
  500: { status: 'failed', message: 'Processing failed due to server error' },
};

/**
 * Parse a status response from the API based on HTTP status code.
 *
 * Interprets status codes according to ContentProcessorAPI behavior:
 * - 200: processing (document is still being processed)
 * - 302: completed (processing finished, redirect to results)
 * - 404: not_found (process ID not found)
 * - 500: error (server error during processing)
 *
 * @param statusCode - The HTTP status code from the response
 * @param response - Optional StatusApiResponse body
 * @returns Object with status, message, and optional resourceUrl
 *
 * Requirements: 4.3
 */
export const parseStatusResponse = (
  statusCode: number,
  response?: StatusApiResponse
): { status: ProcessingStatus; message: string; resourceUrl?: string } => {
  // Get default mapping for status code
  const mapping = STATUS_CODE_MAP[statusCode];

  if (mapping) {
    // For 200 (still processing), use the actual status from the response body
    // which contains the pipeline step name (e.g., "extract", "map", "evaluate", "save")
    let resolvedStatus = mapping.status;
    if (statusCode === 200 && response?.status) {
      resolvedStatus = parseProcessingStatus(response.status);
    }

    return {
      status: resolvedStatus,
      message: response?.message ?? mapping.message,
      resourceUrl: response?.resource_url,
    };
  }

  // Handle unknown status codes
  if (statusCode >= 200 && statusCode < 300) {
    return {
      status: 'processing',
      message: response?.message ?? 'Processing in progress',
      resourceUrl: response?.resource_url,
    };
  }

  if (statusCode >= 400 && statusCode < 500) {
    return {
      status: 'failed',
      message: response?.message ?? 'Request failed',
      resourceUrl: response?.resource_url,
    };
  }

  if (statusCode >= 500) {
    return {
      status: 'failed',
      message: response?.message ?? 'Server error occurred',
      resourceUrl: response?.resource_url,
    };
  }

  // Default fallback
  return {
    status: 'pending',
    message: response?.message ?? 'Unknown status',
    resourceUrl: response?.resource_url,
  };
};

export default parseContentProcessorResponse;
